import React, { useEffect, useState } from 'react';
import ClientPortalLayout from './Layout';
import { clientPortalFetch } from './api';
import { formatCurrency, formatDate, StatusPill } from './Dashboard';
import ReferralJourney from '../../components/ReferralJourney';

const NAVY = '#0f1f45';

function ReferralInner() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showRefer, setShowRefer] = useState(false);
  const [flash, setFlash] = useState(null);

  const load = () =>
    clientPortalFetch('/referral').then(setData).catch((e) => setError(e.message)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  if (loading) return <p style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</p>;
  if (error) return (
    <div style={{ background: '#fff', borderRadius: 14, padding: '40px 24px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>Referral program not enabled</div>
      <p style={{ fontSize: 13, color: '#9ca3af', margin: '8px 0 0' }}>
        {String(error).replace(/\.$/, '')}. If you'd like to refer clients and earn commission, please contact the firm to enable it.
      </p>
    </div>
  );
  if (!data) return null;

  const { partner, leads = [], commissions, redemptions } = data;
  const stageLabel = { lead: 'Lead', task: 'In Progress', invoiced: 'Invoiced', paid: 'Paid', commission_earned: 'Commission Earned', lost: 'Not Converted', pending_verification: 'Under Verification' };
  const canRefer = partner.status === 'active';

  return (
    <div>
      {showRefer && (
        <ReferLeadModal
          onClose={() => setShowRefer(false)}
          onDone={(msg, pending) => { setShowRefer(false); setFlash({ msg, pending }); load(); }}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius: 14, padding: '18px 22px', marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Know someone who needs our services?</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 3 }}>Refer them here — they go straight to our team, and you earn commission when the work is billed.</div>
        </div>
        <button onClick={() => canRefer && setShowRefer(true)} disabled={!canRefer}
          title={canRefer ? '' : 'Your referral partnership is inactive'}
          style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: canRefer ? '#f97316' : '#64748b', color: '#fff', fontSize: 13, fontWeight: 700, cursor: canRefer ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap' }}>
          + Refer a New Lead
        </button>
      </div>

      {flash && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, fontSize: 13, borderRadius: 10, padding: '10px 14px', marginBottom: 20,
          background: flash.pending ? '#fffbeb' : '#f0fdf4', border: `1px solid ${flash.pending ? '#fde68a' : '#bbf7d0'}`, color: flash.pending ? '#b45309' : '#166534' }}>
          <span>{flash.msg}</span>
          <button onClick={() => setFlash(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 14 }}>✕</button>
        </div>
      )}

      <div className="cp-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 }}>
        <StatCard label="Credit Balance" value={formatCurrency(partner.credit_balance)} accent={NAVY} />
        <StatCard label="Total Earned (Net of TDS)" value={formatCurrency(partner.total_earned)} accent="#16a34a" />
        <StatCard label="Total Redeemed" value={formatCurrency(partner.total_redeemed)} accent="#6b7280" />
      </div>

      <PayoutsCard data={data} reload={load} />

      <div style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: 12, color: '#9ca3af' }}>Partner ID</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: NAVY, marginTop: 2 }}>{partner.referral_partner_id}</div>
        {partner.other_company && (
          <div style={{ fontSize: 12, color: '#1d4ed8', marginTop: 6, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '7px 10px' }}>
            This is the Referral account of <b>{partner.partner_client_name || partner.name}</b>. It is the same for all your companies — leads you refer from any of them are credited here.
          </div>
        )}
        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 10 }}>
          Commission: {partner.commission_type === 'percentage' ? `${partner.commission_value}%` : formatCurrency(partner.commission_value)} on {partner.commission_on}
        </div>
        {!partner.pan && (
          <div style={{ fontSize: 12, color: '#b45309', marginTop: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px' }}>
            Your PAN is not on file. Share it with the firm to avoid a higher TDS deduction (Sec 206AA).
          </div>
        )}
      </div>

      <div style={{ background: '#fff', borderRadius: 14, padding: '20px 22px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: NAVY, margin: '0 0 14px' }}>Your Referred Leads &amp; Tasks</h3>
        <p style={{ fontSize: 12, color: '#9ca3af', margin: '-8px 0 14px' }}>Every lead you referred, and where it stands — so you can see exactly which task earned you what.</p>
        {leads.length === 0 && <p style={{ color: '#9ca3af', fontSize: 13 }}>No referrals yet.</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {leads.map((l) => (
            <div key={l.id} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{l.referred_name || 'Unnamed lead'}</div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>
                    {l.task_title ? `Task: ${l.task_title} · ` : ''}Referred {formatDate(l.referral_date)}
                  </div>
                </div>
                <StatusPill status={l.journey?.label || stageLabel[l.status] || l.status} />
              </div>
              {l.journey ? (
                <div style={{ marginTop: 12 }}>
                  <ReferralJourney journey={l.journey} />
                </div>
              ) : (Number(l.invoice_amount) > 0 || Number(l.estimated_commission) > 0) && (
                <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: '#6b7280', flexWrap: 'wrap' }}>
                  {Number(l.invoice_amount) > 0 && <span>Invoice: <strong style={{ color: '#111827' }}>{formatCurrency(l.invoice_amount)}</strong></span>}
                  {l.commission_status ? (
                    <>
                      <span>Commission: <strong style={{ color: '#111827' }}>{formatCurrency(l.commission_amount)}</strong></span>
                      {Number(l.tds_amount) > 0 && <span>TDS ({l.tds_section} @ {l.tds_rate}%): <strong style={{ color: '#dc2626' }}>−{formatCurrency(l.tds_amount)}</strong></span>}
                      <span>{l.commission_status === 'approved' ? 'Net credited' : 'Est. net'}: <strong style={{ color: '#16a34a' }}>{formatCurrency(l.net_amount != null ? l.net_amount : l.commission_amount)}</strong></span>
                    </>
                  ) : (
                    <span>Expected commission: <strong style={{ color: '#f97316' }}>{formatCurrency(l.estimated_commission)}</strong></span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 14, padding: '20px 22px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: NAVY, margin: '0 0 14px' }}>Commissions</h3>
        {commissions.length === 0 && <p style={{ color: '#9ca3af', fontSize: 13 }}>No commissions yet.</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {commissions.map((c) => (
            <div key={c.id} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{c.referred_name || c.task_title || c.service_name || 'Referral commission'}</div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>{formatDate(c.created_at)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>{formatCurrency(c.net_amount != null && c.status === 'approved' ? c.net_amount : c.commission_amount)}</div>
                  <StatusPill status={c.display || c.status} />
                </div>
              </div>
              {c.hold_reason && <div style={{ fontSize: 11, color: '#b45309', marginTop: 4 }}>Not withdrawable yet · {c.hold_reason}</div>}
              {Number(c.tds_amount) > 0 && (
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                  Gross {formatCurrency(c.commission_amount)} − TDS {formatCurrency(c.tds_amount)} ({c.tds_section} @ {c.tds_rate}%) = Net {formatCurrency(c.net_amount)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 14, padding: '20px 22px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: NAVY, margin: '0 0 14px' }}>Redemptions</h3>
        {redemptions.length === 0 && <p style={{ color: '#9ca3af', fontSize: 13 }}>No redemptions yet.</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {redemptions.map((r) => (
            <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#111827', textTransform: 'capitalize' }}>{r.redemption_type}</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>{formatDate(r.processed_at)}</div>
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>{formatCurrency(r.total_amount)}</div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 700px) {
          .cp-cards { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

// ── Payouts: withdrawable balance, bank account on file, payout requests ─────
// Commission is withdrawable only when its invoice is fully paid AND the work
// is completed; an admin of the firm approves every payout.
const REQ_STATUS = { pending: 'Waiting for approval', approved: 'Paid', rejected: 'Declined', cancelled: 'Cancelled' };

function PayoutsCard({ data, reload }) {
  const payout = data.payout || { available: 0, on_hold: 0, pending_requests: 0, requestable: 0 };
  const bank = data.bank;
  const requests = data.payout_requests || [];
  const [mode, setMode] = useState(null); // 'bank' | 'request'
  const [bankForm, setBankForm] = useState({});
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const canRequest = bank?.verified && payout.requestable > 0;
  const why = !bank ? 'Add your bank account to withdraw.'
    : !bank.verified ? 'Your bank account is waiting for the firm to verify it.'
    : payout.requestable > 0 ? null
    : payout.pending_requests > 0 ? 'Your request is with the firm for approval.'
    : 'Commission becomes withdrawable once its invoice is fully paid and the work is completed.';

  const run = async (fn) => {
    setBusy(true); setMsg(null);
    try { const d = await fn(); setMsg({ ok: true, text: d.message || 'Done' }); setMode(null); reload(); }
    catch (e) { setMsg({ ok: false, text: e.message }); }
    setBusy(false);
  };
  const openBank = () => {
    setBankForm({ bank_account_name: bank?.account_name || '', bank_name: bank?.bank_name || '', bank_account_number: '', bank_ifsc: bank?.ifsc || '' });
    setMode('bank'); setMsg(null);
  };
  const openRequest = () => { setAmount(String(payout.requestable || '')); setNote(''); setMode('request'); setMsg(null); };

  const inp = { width: '100%', border: '1px solid #e2e8f0', borderRadius: 9, padding: '9px 12px', fontSize: 13, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const btn = (primary, enabled = true) => ({ padding: '9px 18px', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: enabled ? 'pointer' : 'not-allowed', opacity: enabled ? 1 : 0.55,
    border: primary ? 'none' : '1px solid #e2e8f0', background: primary ? '#f97316' : '#fff', color: primary ? '#fff' : '#4b5563' });

  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: '20px 22px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>Withdraw Commission</h3>
      <p style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 14px' }}>Commission can be withdrawn once its invoice is fully paid and the work is completed. Every payout is approved by the firm.</p>

      <div className="cp-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        <MiniStat label="Withdrawable now" value={formatCurrency(payout.available)} color="#16a34a" />
        <MiniStat label="On hold (invoice / work pending)" value={formatCurrency(payout.on_hold)} color="#b45309" />
        <MiniStat label="Requested, awaiting approval" value={formatCurrency(payout.pending_requests)} color={NAVY} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', border: '1px solid #f1f5f9', borderRadius: 10, padding: '12px 14px' }}>
        <div>
          <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Bank account</div>
          {bank ? (
            <div style={{ fontSize: 13, color: '#111827', marginTop: 3 }}>
              {bank.account_name} · {bank.bank_name} · A/c {bank.account_number} · {bank.ifsc}{' '}
              <span style={{ fontSize: 11, fontWeight: 700, color: bank.verified ? '#16a34a' : '#b45309' }}>{bank.verified ? '✓ Verified' : '• Waiting for verification'}</span>
            </div>
          ) : <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>Not added yet</div>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btn(false, payout.pending_requests === 0)} disabled={payout.pending_requests > 0} onClick={openBank}
            title={payout.pending_requests > 0 ? 'Cancel your pending request to change the account' : ''}>{bank ? 'Change' : 'Add bank account'}</button>
          <button style={btn(true, canRequest)} disabled={!canRequest} onClick={openRequest}>Request payout</button>
        </div>
      </div>
      {why && <p style={{ fontSize: 12, color: '#9ca3af', margin: '8px 0 0' }}>{why}</p>}

      {mode === 'bank' && (
        <div style={{ marginTop: 14, padding: 14, background: '#f8fafc', borderRadius: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="cp-bank-grid">
          <div><label style={lbl}>Account holder name *</label><input style={inp} value={bankForm.bank_account_name} onChange={(e) => setBankForm((f) => ({ ...f, bank_account_name: e.target.value }))} /></div>
          <div><label style={lbl}>Bank name *</label><input style={inp} value={bankForm.bank_name} onChange={(e) => setBankForm((f) => ({ ...f, bank_name: e.target.value }))} /></div>
          <div><label style={lbl}>Account number *{bank ? ` (now ${bank.account_number})` : ''}</label><input style={inp} inputMode="numeric" value={bankForm.bank_account_number} onChange={(e) => setBankForm((f) => ({ ...f, bank_account_number: e.target.value.replace(/\D/g, '') }))} /></div>
          <div><label style={lbl}>IFSC *</label><input style={inp} value={bankForm.bank_ifsc} onChange={(e) => setBankForm((f) => ({ ...f, bank_ifsc: e.target.value.toUpperCase() }))} placeholder="SBIN0001234" /></div>
          <p style={{ gridColumn: '1 / -1', fontSize: 11, color: '#9ca3af', margin: 0 }}>The firm verifies the account before your first payout. Changing it later needs a fresh verification.</p>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button style={btn(false)} onClick={() => setMode(null)}>Cancel</button>
            <button style={btn(true, !busy)} disabled={busy} onClick={() => run(() => clientPortalFetch('/referral/bank', { method: 'PUT', body: JSON.stringify(bankForm) }))}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}

      {mode === 'request' && (
        <div style={{ marginTop: 14, padding: 14, background: '#f8fafc', borderRadius: 10, display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }} className="cp-bank-grid">
          <div><label style={lbl}>Amount * (up to {formatCurrency(payout.requestable)})</label><input style={inp} type="number" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
          <div><label style={lbl}>Note for the firm</label><input style={inp} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" /></div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button style={btn(false)} onClick={() => setMode(null)}>Cancel</button>
            <button style={btn(true, !busy && Number(amount) > 0)} disabled={busy || !(Number(amount) > 0)}
              onClick={() => run(() => clientPortalFetch('/referral/payout-requests', { method: 'POST', body: JSON.stringify({ amount: Number(amount), note }) }))}>{busy ? 'Sending…' : 'Send request'}</button>
          </div>
        </div>
      )}

      {msg && <p style={{ fontSize: 12, margin: '10px 0 0', color: msg.ok ? '#166534' : '#dc2626' }}>{msg.text}</p>}

      {requests.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: NAVY, marginBottom: 6 }}>Your payout requests</div>
          {requests.map((r) => (
            <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{formatCurrency(r.amount)}</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>
                  Requested {formatDate(r.requested_at)}{r.decided_at && r.status !== 'pending' ? ` · ${REQ_STATUS[r.status]} ${formatDate(r.decided_at)}` : ''}
                  {r.decision_note ? ` · Firm's note: ${r.decision_note}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <StatusPill status={REQ_STATUS[r.status] || r.status} />
                {r.status === 'pending' && (
                  <button style={{ ...btn(false), padding: '5px 10px', fontSize: 12 }} disabled={busy}
                    onClick={() => run(() => clientPortalFetch(`/referral/payout-requests/${r.id}/cancel`, { method: 'POST', body: '{}' }))}>Cancel</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <style>{`@media (max-width: 700px) { .cp-bank-grid { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}

const MiniStat = ({ label, value, color }) => (
  <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px' }}>
    <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
  </div>
);

function ReferLeadModal({ onClose, onDone }) {
  const [form, setForm] = useState({ name: '', company: '', phone: '', email: '', notes: '', consent: false });
  const [services, setServices] = useState([]);
  const [picked, setPicked] = useState([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    clientPortalFetch('/referral/services').then((d) => setServices(Array.isArray(d) ? d : [])).catch(() => setServices([]));
  }, []);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const q = search.trim().toLowerCase();
  const visible = q ? services.filter((s) => s.name.toLowerCase().includes(q) || (s.category || '').toLowerCase().includes(q)) : services;
  const canSubmit = form.name.trim() && (form.phone.trim() || form.email.trim()) && form.consent && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setErr('');
    try {
      const d = await clientPortalFetch('/referral/leads', {
        method: 'POST',
        body: JSON.stringify({ ...form, service_ids: picked }),
      });
      onDone(d.message, d.pending_verification);
    } catch (e) {
      setErr(e.message);
    }
    setSaving(false);
  };

  const inp = { width: '100%', border: '1px solid #e2e8f0', borderRadius: 9, padding: '9px 12px', fontSize: 13, outline: 'none', background: '#fff', color: '#111827', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 18, width: 'min(94vw, 780px)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '18px 24px', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', borderRadius: '18px 18px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0 }}>Refer a New Lead</h3>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', margin: '3px 0 0' }}>Our team will contact them and keep you posted here.</p>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', fontSize: 14 }}>✕</button>
        </div>

        <div className="cp-refer-grid" style={{ padding: '20px 24px', overflowY: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          {/* LEFT — contact */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={lbl}>Contact Name *</label>
              <input style={inp} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Person to contact" />
            </div>
            <div>
              <label style={lbl}>Company / Business</label>
              <input style={inp} value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} placeholder="Optional" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={lbl}>Phone</label>
                <input style={inp} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="10-digit mobile" inputMode="tel" />
              </div>
              <div>
                <label style={lbl}>Email</label>
                <input style={inp} type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="name@example.com" />
              </div>
            </div>
            <p style={{ fontSize: 11, color: '#9ca3af', margin: '-6px 0 0' }}>Phone or email is required.</p>
            <div>
              <label style={lbl}>Notes for our team</label>
              <textarea style={{ ...inp, minHeight: 90, resize: 'vertical', fontFamily: 'inherit' }} value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="What do they need? Best time to call?" />
            </div>
          </div>

          {/* RIGHT — services */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
            <label style={{ ...lbl, marginBottom: 0 }}>Services Needed {picked.length > 0 && <span style={{ color: '#f97316' }}>· {picked.length} selected</span>}</label>
            <input style={inp} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search services…" />
            {/* Fixed height + no shrink: as a scrollable flex child its min-height is 0, so on a
                phone (single column, form taller than the screen) it was squeezed to a sliver. */}
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflowY: 'auto', height: 240, flexShrink: 0, padding: 6, WebkitOverflowScrolling: 'touch' }}>
              {visible.length === 0 && <p style={{ fontSize: 12, color: '#9ca3af', padding: 8, margin: 0 }}>No matching services.</p>}
              {visible.map((s) => (
                <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 7, cursor: 'pointer', background: picked.includes(s.id) ? '#fff7ed' : 'transparent' }}>
                  <input type="checkbox" checked={picked.includes(s.id)} onChange={() => toggle(s.id)} />
                  <span style={{ fontSize: 13, color: '#111827', flex: 1 }}>{s.name}</span>
                  {s.category && <span style={{ fontSize: 10, color: '#9ca3af' }}>{s.category}</span>}
                </label>
              ))}
            </div>
            <p style={{ fontSize: 11, color: '#9ca3af', margin: 0 }}>Not sure? Leave blank and describe it in the notes.</p>
          </div>
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid #f1f5f9', flexShrink: 0 }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
            <input type="checkbox" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} style={{ marginTop: 2 }} />
            <span style={{ fontSize: 12, color: '#4b5563' }}>I confirm this person has agreed to be contacted and to have their details shared with the firm.</span>
          </label>
          {err && <p style={{ fontSize: 12, color: '#dc2626', margin: '0 0 10px' }}>{err}</p>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 9, border: '1px solid #e2e8f0', background: '#fff', color: '#4b5563', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button onClick={submit} disabled={!canSubmit}
              style={{ padding: '9px 22px', borderRadius: 9, border: 'none', background: '#f97316', color: '#fff', fontSize: 13, fontWeight: 700, cursor: canSubmit ? 'pointer' : 'not-allowed', opacity: canSubmit ? 1 : 0.55 }}>
              {saving ? 'Submitting…' : 'Submit Referral'}
            </button>
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 700px) { .cp-refer-grid { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}

const StatCard = ({ label, value, accent }) => (
  <div style={{ background: '#fff', borderRadius: 14, padding: '20px 22px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
    <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500, marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 700, color: accent || NAVY }}>{value}</div>
  </div>
);

function Referral() {
  return <ClientPortalLayout title="Referral Commissions"><ReferralInner /></ClientPortalLayout>;
}

export default Referral;
